/**
 * Utilities for HTML sanitization to prevent Cross-Site Scripting (XSS).
 */

const ALLOWED_TAGS = new Set([
  'B', 'STRONG',
  'I', 'EM',
  'U',
  'P', 'BR',
  'UL', 'OL', 'LI',
  'A',
]);

const SAFE_URL_PATTERN = /^(?:https?:\/\/|mailto:)/i;

/**
 * Sanitizes rich text HTML for display, allowing only basic formatting tags
 * (b, strong, i, em, u, p, br, ul, ol, li, a) and safe links.
 * All scripts, dangerous tags, inline event handlers, and javascript: URLs are stripped.
 */
export function sanitizeNoteHtml(dirty: string): string {
  if (!dirty) return '';

  if (typeof DOMParser === 'undefined') {
    // Basic fallback for non-DOM environments: strip all HTML tags
    return dirty.replace(/<[^>]*>/g, '');
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(dirty, 'text/html');

  // Remove completely forbidden elements and their content
  const forbiddenSelectors = ['script', 'style', 'iframe', 'object', 'embed', 'form', 'svg', 'math', 'base', 'meta', 'link'];
  for (const selector of forbiddenSelectors) {
    const dangerousElements = doc.body.querySelectorAll(selector);
    dangerousElements.forEach((el) => el.remove());
  }

  // Recursively sanitize all remaining nodes
  function cleanNode(node: Node): void {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        const tagName = el.tagName.toUpperCase();

        if (!ALLOWED_TAGS.has(tagName)) {
          // Replace disallowed tag with its safe child text/nodes
          cleanNode(el);
          while (el.firstChild) {
            el.parentNode?.insertBefore(el.firstChild, el);
          }
          el.remove();
        } else {
          // Remove all attributes except safe ones
          const attrs = Array.from(el.attributes);
          for (const attr of attrs) {
            const attrName = attr.name.toLowerCase();
            if (tagName === 'A' && attrName === 'href') {
              const hrefValue = attr.value.trim();
              if (SAFE_URL_PATTERN.test(hrefValue)) {
                el.setAttribute('href', hrefValue);
              } else {
                el.removeAttribute('href');
              }
            } else {
              el.removeAttribute(attr.name);
            }
          }

          if (tagName === 'A') {
            el.setAttribute('target', '_blank');
            el.setAttribute('rel', 'noopener noreferrer');
          }

          cleanNode(el);
        }
      }
    }
  }

  cleanNode(doc.body);
  return doc.body.innerHTML;
}

/**
 * Extracts plain text from an HTML snippet safely without executing any scripts.
 */
export function stripHtmlToText(dirty: string): string {
  if (!dirty) return '';

  if (typeof DOMParser === 'undefined') {
    return dirty.replace(/<[^>]*>/g, '').trim();
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(dirty, 'text/html');

  // Remove dangerous tags before extracting text
  const dangerousElements = doc.body.querySelectorAll('script, style, iframe');
  dangerousElements.forEach((el) => el.remove());

  return ((doc.body.textContent ?? '')).trim();
}
